import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { QRCodeSVG } from 'qrcode.react';
import { 
  ArrowLeft, FileText, MapPin, Calendar, Truck, Package,
  Leaf, Factory, Warehouse, Ship, Award, CheckCircle,
  Plus, Save, ChevronRight, Globe, Thermometer, Trash2, Eye,
  QrCode, Download, Camera, MapPinned, Upload, Image, X, Link2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TraceabilityDossier = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isNew = id === 'new';
  const fileInputRef = useRef(null);
  const qrRef = useRef(null);
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [dossiers, setDossiers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selectedShipmentId, setSelectedShipmentId] = useState('');
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [uploadedImages, setUploadedImages] = useState({
    farm: [],
    harvest: [],
    processing: [],
    storage: [],
    transport: []
  });
  const [gettingLocation, setGettingLocation] = useState(false);
  const [formData, setFormData] = useState({
    product_name: '',
    product_category: 'Spices',
    origin_country: 'India',
    destination_country: '',
    farm_details: {
      farm_name: '',
      farmer_name: '',
      location: '',
      gps_coordinates: '',
      country: 'India',
      state_region: '',
      cultivation_method: 'Organic',
      soil_type: '',
      irrigation_method: '',
      images: []
    },
    harvest_details: {
      harvest_date: '',
      quantity_kg: '',
      quality_grade: '',
      weather_conditions: '',
      harvest_method: '',
      images: []
    },
    processing_details: {
      facility_name: '',
      facility_location: '',
      processing_date: '',
      processing_method: '',
      batch_size_kg: '',
      output_quantity_kg: '',
      images: []
    },
    storage_details: {
      warehouse_name: '',
      warehouse_location: '',
      storage_date: '',
      storage_conditions: '',
      temperature: '',
      humidity: '',
      duration_days: '',
      images: []
    },
    transport_details: {
      transport_mode: 'Sea',
      vehicle_type: '',
      departure_date: '',
      arrival_date: '',
      origin: '',
      destination: '',
      cold_chain: false,
      container_number: '',
      images: []
    },
    certifications: []
  });

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: 'Error', description: 'Geolocation is not supported by your browser', variant: 'destructive' });
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const coordString = `${latitude.toFixed(4)}° N, ${longitude.toFixed(4)}° E`;
        setFormData(prev => ({
          ...prev,
          farm_details: {
            ...prev.farm_details,
            gps_coordinates: coordString
          }
        }));
        toast({ title: 'Location Captured', description: `Accuracy: ${accuracy.toFixed(0)}m` });
        setGettingLocation(false);
      },
      (error) => {
        toast({ title: 'Location Error', description: error.message, variant: 'destructive' });
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Handle file upload
  const handleFileUpload = async (event, section) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newImages = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        newImages.push({
          name: file.name,
          type: file.type,
          data: base64Data,
          preview: base64Data
        });
        
        if (newImages.length === files.length) {
          setUploadedImages(prev => ({
            ...prev,
            [section]: [...prev[section], ...newImages]
          }));
          
          toast({ 
            title: 'Images Added', 
            description: `${files.length} image(s) added to ${section}` 
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove uploaded image
  const removeImage = (section, index) => {
    setUploadedImages(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  // Download QR code
  const downloadQR = () => {
    if (!qrRef.current) return;
    
    const svg = qrRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `trace-${selectedDossier?.batch_id || 'qr'}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const getTraceUrl = (batchId) => {
    return `${window.location.origin}/trace/${batchId}`;
  };

  useEffect(() => {
    fetchShipments();
    if (!isNew && id) {
      fetchDossier(id);
    } else if (!id || isNew) {
      fetchAllDossiers();
    }
  }, [id, isNew]);

  const fetchShipments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eximstack/shipments`);
      const data = await response.json();
      setShipments(data.shipments || []);
    } catch (error) {
      console.error('Error fetching shipments:', error);
    }
  };

  const fetchAllDossiers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eximstack/dossiers`);
      const data = await response.json();
      setDossiers(data.dossiers || []);
    } catch (error) {
      console.error('Error fetching dossiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDossier = async (dossierId) => {
    try {
      const response = await fetch(`${API_URL}/api/eximstack/dossiers/${dossierId}`);
      const data = await response.json();
      setSelectedDossier(data);
      setFormData(data);
    } catch (error) {
      console.error('Error fetching dossier:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Clean up form data - remove empty optional sections
      const cleanedData = {
        product_name: formData.product_name,
        product_category: formData.product_category,
        origin_country: formData.origin_country,
        destination_country: formData.destination_country,
        shipment_id: selectedShipmentId || null,
        farm_details: {
          farm_name: formData.farm_details.farm_name,
          farmer_name: formData.farm_details.farmer_name || null,
          location: formData.farm_details.location,
          gps_coordinates: formData.farm_details.gps_coordinates || null,
          country: formData.farm_details.country,
          state_region: formData.farm_details.state_region || null,
          cultivation_method: formData.farm_details.cultivation_method,
          soil_type: formData.farm_details.soil_type || null,
          irrigation_method: formData.farm_details.irrigation_method || null
        },
        harvest_details: {
          harvest_date: formData.harvest_details.harvest_date,
          quantity_kg: formData.harvest_details.quantity_kg ? parseFloat(formData.harvest_details.quantity_kg) : null,
          quality_grade: formData.harvest_details.quality_grade || null,
          weather_conditions: formData.harvest_details.weather_conditions || null,
          harvest_method: formData.harvest_details.harvest_method || null
        },
        certifications: formData.certifications || []
      };
      
      // Only include processing_details if facility_name is provided
      if (formData.processing_details.facility_name) {
        cleanedData.processing_details = {
          facility_name: formData.processing_details.facility_name,
          facility_location: formData.processing_details.facility_location || '',
          processing_date: formData.processing_details.processing_date || '',
          processing_method: formData.processing_details.processing_method || '',
          batch_size_kg: formData.processing_details.batch_size_kg ? parseFloat(formData.processing_details.batch_size_kg) : 0,
          output_quantity_kg: formData.processing_details.output_quantity_kg ? parseFloat(formData.processing_details.output_quantity_kg) : null
        };
      }
      
      // Only include storage_details if warehouse_name is provided
      if (formData.storage_details.warehouse_name) {
        cleanedData.storage_details = {
          warehouse_name: formData.storage_details.warehouse_name,
          warehouse_location: formData.storage_details.warehouse_location || '',
          storage_date: formData.storage_details.storage_date || '',
          storage_conditions: formData.storage_details.storage_conditions || null,
          temperature: formData.storage_details.temperature || null,
          humidity: formData.storage_details.humidity || null,
          duration_days: formData.storage_details.duration_days ? parseInt(formData.storage_details.duration_days) : null
        };
      }
      
      // Only include transport_details if departure_date is provided
      if (formData.transport_details.departure_date) {
        cleanedData.transport_details = {
          transport_mode: formData.transport_details.transport_mode,
          vehicle_type: formData.transport_details.vehicle_type || null,
          departure_date: formData.transport_details.departure_date,
          arrival_date: formData.transport_details.arrival_date || null,
          origin: formData.transport_details.origin || '',
          destination: formData.transport_details.destination || '',
          cold_chain: formData.transport_details.cold_chain || false,
          container_number: formData.transport_details.container_number || null
        };
      }
      
      const response = await fetch(`${API_URL}/api/eximstack/dossiers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanedData)
      });
      const data = await response.json();
      if (response.ok) {
        toast({ title: 'Success', description: 'Dossier created successfully!' });
        navigate(`/eximstack/traceability/${data.dossier.id}`);
      } else {
        throw new Error(data.detail || 'Failed to create dossier');
      }
    } catch (error) {
      console.error('Error saving dossier:', error);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const deleteDossier = async (dossierId) => {
    if (!window.confirm('Are you sure you want to delete this dossier?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/eximstack/dossiers/${dossierId}`, { method: 'DELETE' });
      if (response.ok) {
        toast({ title: 'Deleted', description: 'Dossier deleted successfully' });
        fetchAllDossiers();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete dossier', variant: 'destructive' });
    }
  };

  const timelineStages = [
    { key: 'farm', icon: Leaf, label: 'Farm Source', color: '#4CAF50', data: selectedDossier?.farm_details },
    { key: 'harvest', icon: Calendar, label: 'Harvest', color: '#8BC34A', data: selectedDossier?.harvest_details },
    { key: 'processing', icon: Factory, label: 'Processing', color: '#FF9800', data: selectedDossier?.processing_details },
    { key: 'storage', icon: Warehouse, label: 'Storage', color: '#2196F3', data: selectedDossier?.storage_details },
    { key: 'transport', icon: Ship, label: 'Transport', color: '#9C27B0', data: selectedDossier?.transport_details },
  ];

  // List View
  if (!id) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link to="/eximstack" className="text-sm text-gray-500 hover:text-[#263776] flex items-center gap-1 mb-2">
                <ArrowLeft className="w-4 h-4" /> Back to EximStack
              </Link>
              <h1 className="text-3xl font-bold text-[#263776]">Traceability Dossiers</h1>
              <p className="text-gray-600">Farm to Fork documentation for all shipments</p>
            </div>
            <Link to="/eximstack/traceability/new">
              <Button className="bg-[#4CAF50] hover:bg-[#43a047]">
                <Plus className="w-4 h-4 mr-2" /> Create New Dossier
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : dossiers.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Dossiers Yet</h3>
                <p className="text-gray-500 mb-4">Create your first traceability dossier to get started</p>
                <Link to="/eximstack/traceability/new">
                  <Button className="bg-[#4CAF50]">Create Dossier</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dossiers.map((dossier) => (
                <Card key={dossier.id} className="hover:shadow-lg transition-shadow h-full" data-testid={`dossier-card-${dossier.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <Badge className="bg-green-100 text-green-800 mb-2">
                          {dossier.product_category}
                        </Badge>
                        <Badge className={
                          dossier.status === 'Shipped' ? 'bg-purple-100 text-purple-800' :
                          dossier.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          'bg-blue-100 text-blue-800'
                        }>
                          {dossier.status}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg">{dossier.product_name}</CardTitle>
                      <p className="text-sm text-gray-500 font-mono">{dossier.batch_id}</p>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                        <Globe className="w-4 h-4" />
                        <span>{dossier.origin_country}</span>
                        <ChevronRight className="w-4 h-4" />
                        <span>{dossier.destination_country}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                        <MapPin className="w-4 h-4" />
                        <span>{dossier.farm_details?.location}</span>
                      </div>
                      <div className="flex items-center gap-2 pt-3 border-t">
                        <Link to={`/eximstack/traceability/${dossier.id}`} className="flex-1">
                          <Button variant="outline" size="sm" className="w-full">
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => deleteDossier(dossier.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Create New Form
  if (isNew) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/eximstack/traceability" className="text-sm text-gray-500 hover:text-[#263776] flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Dossiers
          </Link>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-[#263776] flex items-center gap-2">
                <FileText className="w-6 h-6 text-[#4CAF50]" />
                Create Traceability Dossier
              </CardTitle>
              <p className="text-gray-600">Enter farm-to-fork details for your shipment</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Shipment Linking */}
                {shipments.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Link2 className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-blue-800">Link to Shipment (Optional)</h3>
                    </div>
                    <select 
                      className="w-full border border-blue-300 rounded-md p-2 bg-white"
                      value={selectedShipmentId}
                      onChange={(e) => {
                        setSelectedShipmentId(e.target.value);
                        // Auto-fill origin and destination from shipment
                        const shipment = shipments.find(s => s.id === e.target.value);
                        if (shipment) {
                          setFormData(prev => ({
                            ...prev,
                            origin_country: shipment.origin || prev.origin_country,
                            destination_country: shipment.destination || prev.destination_country,
                            product_name: shipment.product_name || prev.product_name
                          }));
                        }
                      }}
                    >
                      <option value="">-- Create as standalone dossier --</option>
                      {shipments.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.batch_number} - {s.product_name} ({s.shipment_type}: {s.origin} → {s.destination})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-blue-600 mt-2">
                      Linking to a shipment will auto-fill origin/destination and group related records together.
                    </p>
                  </div>
                )}

                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#263776] border-b pb-2">Basic Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Product Name *</Label>
                      <Input 
                        value={formData.product_name}
                        onChange={(e) => handleInputChange(null, 'product_name', e.target.value)}
                        placeholder="e.g., Premium Turmeric Fingers"
                        required
                      />
                    </div>
                    <div>
                      <Label>Product Category *</Label>
                      <select 
                        className="w-full border rounded-md p-2"
                        value={formData.product_category}
                        onChange={(e) => handleInputChange(null, 'product_category', e.target.value)}
                      >
                        <option value="Spices">Spices</option>
                        <option value="Dry Fruits">Dry Fruits</option>
                        <option value="Grains">Grains & Pulses</option>
                        <option value="Edible Oils">Edible Oils</option>
                        <option value="Sugar">Sugar</option>
                        <option value="Fresh Produce">Fresh Produce</option>
                      </select>
                    </div>
                    <div>
                      <Label>Origin Country *</Label>
                      <Input 
                        value={formData.origin_country}
                        onChange={(e) => handleInputChange(null, 'origin_country', e.target.value)}
                        placeholder="e.g., India"
                        required
                      />
                    </div>
                    <div>
                      <Label>Destination Country *</Label>
                      <Input 
                        value={formData.destination_country}
                        onChange={(e) => handleInputChange(null, 'destination_country', e.target.value)}
                        placeholder="e.g., United Arab Emirates"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Farm Details */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#263776] border-b pb-2 flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-600" /> Farm Source Details
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Farm Name *</Label>
                      <Input 
                        value={formData.farm_details.farm_name}
                        onChange={(e) => handleInputChange('farm_details', 'farm_name', e.target.value)}
                        placeholder="e.g., Organic Valley Farm"
                        required
                      />
                    </div>
                    <div>
                      <Label>Farmer Name</Label>
                      <Input 
                        value={formData.farm_details.farmer_name}
                        onChange={(e) => handleInputChange('farm_details', 'farmer_name', e.target.value)}
                        placeholder="e.g., Mr. Ramesh Kumar"
                      />
                    </div>
                    <div>
                      <Label>Location *</Label>
                      <Input 
                        value={formData.farm_details.location}
                        onChange={(e) => handleInputChange('farm_details', 'location', e.target.value)}
                        placeholder="e.g., Erode District, Tamil Nadu"
                        required
                      />
                    </div>
                    <div>
                      <Label>GPS Coordinates</Label>
                      <div className="flex gap-2">
                        <Input 
                          value={formData.farm_details.gps_coordinates}
                          onChange={(e) => handleInputChange('farm_details', 'gps_coordinates', e.target.value)}
                          placeholder="e.g., 11.3410° N, 77.7172° E"
                          className="flex-1"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={getCurrentLocation}
                          disabled={gettingLocation}
                          className="px-3"
                        >
                          {gettingLocation ? (
                            <div className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full" />
                          ) : (
                            <MapPinned className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Tap button to capture current location</p>
                    </div>
                    <div>
                      <Label>State/Region</Label>
                      <Input 
                        value={formData.farm_details.state_region}
                        onChange={(e) => handleInputChange('farm_details', 'state_region', e.target.value)}
                        placeholder="e.g., Tamil Nadu"
                      />
                    </div>
                    <div>
                      <Label>Cultivation Method *</Label>
                      <select 
                        className="w-full border rounded-md p-2"
                        value={formData.farm_details.cultivation_method}
                        onChange={(e) => handleInputChange('farm_details', 'cultivation_method', e.target.value)}
                      >
                        <option value="Organic">Organic</option>
                        <option value="Conventional">Conventional</option>
                        <option value="GAP Certified">GAP Certified</option>
                        <option value="Traditional">Traditional</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Farm Images Upload */}
                  <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <Label className="flex items-center gap-2 mb-2">
                      <Camera className="w-4 h-4 text-green-600" />
                      Farm Images (Optional)
                    </Label>
                    <p className="text-xs text-gray-500 mb-3">Upload photos of the farm, crops, or cultivation practices</p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'farm')}
                    />
                    <div className="flex flex-wrap gap-2">
                      {uploadedImages.farm.map((img, idx) => (
                        <div key={idx} className="relative">
                          <img 
                            src={img.preview} 
                            alt={`Farm ${idx + 1}`} 
                            className="w-16 h-16 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage('farm', idx)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-16 h-16 border-2 border-dashed border-green-300 rounded-lg flex flex-col items-center justify-center text-green-600 hover:bg-green-100 transition-colors"
                      >
                        <Camera className="w-5 h-5" />
                        <span className="text-xs mt-1">Add</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Harvest Details */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#263776] border-b pb-2 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-lime-600" /> Harvest Details
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Harvest Date *</Label>
                      <Input 
                        type="date"
                        value={formData.harvest_details.harvest_date}
                        onChange={(e) => handleInputChange('harvest_details', 'harvest_date', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label>Quantity (kg) *</Label>
                      <Input 
                        type="number"
                        value={formData.harvest_details.quantity_kg}
                        onChange={(e) => handleInputChange('harvest_details', 'quantity_kg', e.target.value)}
                        placeholder="e.g., 5000"
                        required
                      />
                    </div>
                    <div>
                      <Label>Quality Grade</Label>
                      <Input 
                        value={formData.harvest_details.quality_grade}
                        onChange={(e) => handleInputChange('harvest_details', 'quality_grade', e.target.value)}
                        placeholder="e.g., Premium Export Grade"
                      />
                    </div>
                    <div>
                      <Label>Harvest Method</Label>
                      <Input 
                        value={formData.harvest_details.harvest_method}
                        onChange={(e) => handleInputChange('harvest_details', 'harvest_method', e.target.value)}
                        placeholder="e.g., Manual harvesting"
                      />
                    </div>
                  </div>
                </div>

                {/* Processing Details */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#263776] border-b pb-2 flex items-center gap-2">
                    <Factory className="w-5 h-5 text-orange-600" /> Processing Details
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Facility Name</Label>
                      <Input 
                        value={formData.processing_details.facility_name}
                        onChange={(e) => handleInputChange('processing_details', 'facility_name', e.target.value)}
                        placeholder="e.g., SPANXPORT Processing Unit"
                      />
                    </div>
                    <div>
                      <Label>Facility Location</Label>
                      <Input 
                        value={formData.processing_details.facility_location}
                        onChange={(e) => handleInputChange('processing_details', 'facility_location', e.target.value)}
                        placeholder="e.g., Chennai, Tamil Nadu"
                      />
                    </div>
                    <div>
                      <Label>Processing Date</Label>
                      <Input 
                        type="date"
                        value={formData.processing_details.processing_date}
                        onChange={(e) => handleInputChange('processing_details', 'processing_date', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Processing Method</Label>
                      <Input 
                        value={formData.processing_details.processing_method}
                        onChange={(e) => handleInputChange('processing_details', 'processing_method', e.target.value)}
                        placeholder="e.g., Cleaning, Grading, Sorting"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-4 border-t">
                  <Link to="/eximstack/traceability">
                    <Button type="button" variant="outline">Cancel</Button>
                  </Link>
                  <Button type="submit" className="bg-[#4CAF50]" disabled={saving}>
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Create Dossier'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Detail View with Timeline
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!selectedDossier) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-4">Dossier not found</h2>
          <Link to="/eximstack/traceability">
            <Button>Back to Dossiers</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <Link to="/eximstack/traceability" className="text-sm text-gray-500 hover:text-[#263776] flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Dossiers
        </Link>

        {/* Header */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-green-100 text-green-800">{selectedDossier.product_category}</Badge>
                  <Badge className={
                    selectedDossier.status === 'Shipped' ? 'bg-purple-100 text-purple-800' :
                    selectedDossier.status === 'Completed' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }>
                    {selectedDossier.status}
                  </Badge>
                </div>
                <h1 className="text-2xl font-bold text-[#263776]">{selectedDossier.product_name}</h1>
                <p className="text-gray-500 font-mono">{selectedDossier.batch_id}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 text-lg">
                  <span className="font-medium">{selectedDossier.origin_country}</span>
                  <Truck className="w-5 h-5 text-[#F47D20]" />
                  <span className="font-medium">{selectedDossier.destination_country}</span>
                </div>
                <p className="text-sm text-gray-500">
                  Created: {new Date(selectedDossier.created_at).toLocaleDateString()}
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setShowQRModal(true)}
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  View QR Code
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* QR Code Modal */}
        {showQRModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowQRModal(false)}>
            <Card className="max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              <CardHeader className="text-center">
                <CardTitle className="text-lg">Traceability QR Code</CardTitle>
                <p className="text-sm text-gray-500">Scan to view shipment details</p>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <div ref={qrRef} className="bg-white p-4 rounded-lg border">
                  <QRCodeSVG 
                    value={getTraceUrl(selectedDossier.batch_id)}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center break-all">
                  {getTraceUrl(selectedDossier.batch_id)}
                </p>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={downloadQR}>
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Link to={`/trace/${selectedDossier.batch_id}`} target="_blank">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700">
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Timeline */}
        <h2 className="text-xl font-bold text-[#263776] mb-4">Farm to Fork Journey</h2>
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block"></div>
          
          <div className="space-y-6">
            {timelineStages.map((stage, idx) => (
              <Card key={stage.key} className={`relative ${stage.data ? '' : 'opacity-50'}`}>
                {/* Timeline Dot */}
                <div 
                  className="absolute left-4 top-6 w-8 h-8 rounded-full flex items-center justify-center z-10 hidden md:flex"
                  style={{ backgroundColor: stage.color }}
                >
                  <stage.icon className="w-4 h-4 text-white" />
                </div>
                
                <CardContent className="p-6 md:ml-16">
                  <div className="flex items-center gap-2 mb-3">
                    <stage.icon className="w-5 h-5 md:hidden" style={{ color: stage.color }} />
                    <h3 className="font-semibold text-lg" style={{ color: stage.color }}>{stage.label}</h3>
                    {stage.data && <CheckCircle className="w-5 h-5 text-green-500" />}
                  </div>
                  
                  {stage.data ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(stage.data).map(([key, value]) => {
                        if (!value || key === 'country') return null;
                        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        return (
                          <div key={key}>
                            <p className="text-sm text-gray-500">{label}</p>
                            <p className="font-medium">{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-400 italic">Details not yet added</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Certifications */}
        {selectedDossier.certifications && selectedDossier.certifications.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-[#263776] mb-4 flex items-center gap-2">
              <Award className="w-6 h-6 text-amber-500" /> Certifications
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedDossier.certifications.map((cert, idx) => (
                <Card key={idx} className="border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-[#263776]">{cert.certification_name}</h4>
                    <p className="text-sm text-gray-600">{cert.certifying_body}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Valid: {cert.valid_from} to {cert.valid_until}
                    </p>
                    <Badge className={cert.status === 'Active' ? 'bg-green-100 text-green-800 mt-2' : 'bg-gray-100 text-gray-800 mt-2'}>
                      {cert.status}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TraceabilityDossier;
